import { env } from '../config/env';
import { CopyAnalysisResult, CopyIssue } from '../types/copyAnalysis';
import { generatePrompt } from '../config/promptConfig';

interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export async function getChatCompletion(messages: ChatMessage[]) {
  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${env.VITE_OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4',
        messages,
        temperature: 0.7,
        max_tokens: 2000,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error('Error in getChatCompletion:', error);
    throw error;
  }
}

function parseIssue(text: string): CopyIssue | null {
  try {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 5) return null;

    const solution = lines[4].replace(/^Solution:\s*/i, '').trim();
    const quotedSolution = ensureQuotedSolution(solution);

    return {
      id: 0, // Will be set later
      category: lines[0].replace(/^Category:\s*/i, '').trim(),
      title: lines[1].replace(/^Title:\s*/i, '').trim(),
      currentState: lines[2].replace(/^Current State:\s*/i, '').trim(),
      importance: lines[3].replace(/^Importance:\s*/i, '').trim(),
      solution: quotedSolution
    };
  } catch (error) {
    console.error('Error parsing issue:', error);
    return null;
  }
}

function ensureQuotedSolution(solution: string): string {
  if (/"([^"]+)"/.test(solution)) {
    return solution;
  }

  const changePatterns = [
    /(?:change|update|replace|modify)(?:\s+(?:to|with))?\s*["']([^"']+)["']/i,
    /(?:use|try|suggest|implement|add)\s+["']([^"']+)["']/i,
    /new\s+(?:headline|copy|text|version):\s*["']([^"']+)["']/i
  ];

  for (const pattern of changePatterns) {
    const match = solution.match(pattern);
    if (match) {
      return `Change to "${match[1]}"`;
    }
  }

  const cleanSolution = solution.replace(/['"]/g, '');
  return `Use "${cleanSolution}"`;
}

function removeDuplicateIssues(issues: CopyIssue[]): CopyIssue[] {
  const seenCurrentStates = new Set<string>();
  const uniqueIssues: CopyIssue[] = [];

  for (const issue of issues) {
    const normalizedCurrentState = issue.currentState
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');

    if (!seenCurrentStates.has(normalizedCurrentState)) {
      seenCurrentStates.add(normalizedCurrentState);
      uniqueIssues.push(issue);
    }
  }

  return uniqueIssues;
}

export async function analyzeCopyWithGPT4(text: string): Promise<CopyAnalysisResult> {
  try {
    const messages: ChatMessage[] = [
      {
        role: 'system',
        content: generatePrompt(text)
      }
    ];

    const analysisText = await getChatCompletion(messages);

    const issues = analysisText
      .split('\n\n')
      .map(parseIssue)
      .filter((issue): issue is CopyIssue => issue !== null);

    const uniqueIssues = removeDuplicateIssues(issues)
      .map((issue, index) => ({ ...issue, id: index + 1 }))
      .slice(0, 17);

    if (uniqueIssues.length === 0) {
      throw new Error('Failed to parse any valid issues from the response');
    }

    return {
      issues: uniqueIssues,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error analyzing copy:', error);
    throw error instanceof Error ? error : new Error('Failed to analyze copy');
  }
}